import type { AnalysisType, VOI } from 'config/analysisConfig';
import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import type vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import { getAllowedRasterImageLayersForAnalysisType } from './cprImageResources';
import type {
  CprMode,
  ImageTileRect,
  ImageLayerKey,
  ResolvedImageLayerPaths,
} from './cprImageResources';

export type LabelMap2D = {
  width: number;
  height: number;
  data: Uint8Array;
};

export type RgbaImageData = {
  rgba: Uint8ClampedArray;
  width: number;
  height: number;
};

type ParsedOverlayPath =
  | {
      kind: 'cpr';
      seriesId: string;
      branchId: number;
      mode: CprMode;
      angleDeg: number;
      index: null;
      layer: ImageLayerKey;
    }
  | {
      kind: 'cross-section';
      seriesId: string;
      branchId: number;
      mode: null;
      angleDeg: 0;
      index: number;
      layer: ImageLayerKey;
    };

type LocalOverlayContext = {
  analysisType?: AnalysisType;
  config?: Record<string, any>;
  currentVOI?: VOI;
  pool?: Record<string, any>;
  huImageData?: vtkImageData;
  labelDataByBranch?: Record<number, vtkImageData>;
  centerDataByBranch?: Record<number, vtkPolyData>;
  aorticLabelData?: vtkImageData;
  forceCprLocalOverlayFallback?: boolean;
};

export type OverlayPaintPolicy = {
  analysisType?: AnalysisType;
  config?: Record<string, any>;
};

type RegisteredLocalOverlayContext = LocalOverlayContext & {
  version: number;
  signature: string;
};

const contexts = new Map<string, RegisteredLocalOverlayContext>();
const contextVersionListeners = new Map<
  string,
  Set<(version: number) => void>
>();
const crossSectionLabelMapCache = new Map<string, LabelMap2D | null>();
let contextVersion = 0;
const objectIdentity = new WeakMap<object, number>();
let nextObjectIdentity = 1;

const LOCAL_OVERLAY_PREFIX = 'local-overlay://';
const OVERLAY_LAYER_ORDER: ImageLayerKey[] = [
  'lumen',
  'lumenoutline',
  'plaque',
  'plaqueoutline',
  'fai',
  'aorta',
  'avc',
];

function stripQuery(path: string) {
  return path.split('?')[0];
}

function parsePath(path: string): ParsedOverlayPath | null {
  const clean = stripQuery(path);
  const parts = clean.split('/');
  const isLocal = clean.startsWith(LOCAL_OVERLAY_PREFIX);
  const isMockCpr = clean.startsWith('mock://cpr/');
  const isMockCrossSection = clean.startsWith('mock://cross-section/');

  if (isLocal && parts[2] === 'cpr') {
    return {
      kind: 'cpr',
      seriesId: parts[3],
      branchId: Number(parts[4]),
      mode: parts[5] as CprMode,
      angleDeg: Number(parts[6]),
      index: null,
      layer: (parts[7] ?? 'base') as ImageLayerKey,
    };
  }

  if (isLocal && parts[2] === 'cross-section') {
    return {
      kind: 'cross-section',
      seriesId: parts[3],
      branchId: Number(parts[4]),
      mode: null,
      angleDeg: 0,
      index: Number(parts[5]),
      layer: (parts[6] ?? 'base') as ImageLayerKey,
    };
  }

  if (isMockCpr) {
    return {
      kind: 'cpr',
      seriesId: parts[3],
      branchId: Number(parts[4]),
      mode: parts[5] as CprMode,
      angleDeg: Number(parts[6]),
      index: null,
      layer: (parts[7] ?? 'base') as ImageLayerKey,
    };
  }

  if (isMockCrossSection) {
    return {
      kind: 'cross-section',
      seriesId: parts[3],
      branchId: Number(parts[4]),
      mode: null,
      angleDeg: 0,
      index: Number(parts[5]),
      layer: (parts[6] ?? 'base') as ImageLayerKey,
    };
  }

  return null;
}

function hexToRgba(
  hex: string | undefined,
  alpha: number,
): [number, number, number, number] {
  const fallback: [number, number, number, number] = [255, 255, 255, alpha];
  if (!hex || typeof hex !== 'string') return fallback;
  const clean = hex.replace('#', '').trim();
  if (![3, 6].includes(clean.length)) return fallback;
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => `${c}${c}`)
          .join('')
      : clean;
  const value = Number.parseInt(full, 16);
  if (!Number.isFinite(value)) return fallback;
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255, alpha];
}

function isAorticAnalysis(analysisType?: AnalysisType) {
  return analysisType === 'aorticValveCalcium';
}

export function isCprLocalOverlayFallbackEnabled(config?: Record<string, any>) {
  const runtimeConfig =
    typeof window !== 'undefined'
      ? ((window as any).runtimeConfig ?? (window as any).__APP_CONFIG__)
      : undefined;
  const raw =
    config?.CPR_CROSS_SECTION_CPR_LOCAL_OVERLAY ??
    runtimeConfig?.CPR_CROSS_SECTION_CPR_LOCAL_OVERLAY;
  return raw === true || raw === '1' || raw === 'true';
}

export function getVisibleLabelValuesForLayer(
  analysisType: AnalysisType | undefined,
  layer: ImageLayerKey,
  config: Record<string, any> | undefined,
): number[] {
  if (layer === 'lumen' || layer === 'lumenoutline') return [255];

  if (analysisType === 'plaque') {
    if (layer === 'plaque') return [1, 2, 3];
    if (layer === 'plaqueoutline') return [1, 2, 3, 255];
    return [];
  }

  if (analysisType === 'fai') {
    if (layer === 'fai') return [251, 252, 253, 254];
    return [];
  }

  if (analysisType === 'stenosis' || analysisType === 'isr') {
    return [];
  }

  if (isAorticAnalysis(analysisType)) {
    if (layer === 'avc') return config?.avc_indexs ?? [];
    if (layer === 'aorta') {
      return (config?.aorta_opacitys ?? [])
        .filter((entry: [number, number]) => Number(entry?.[1]) > 0)
        .map((entry: [number, number]) => Number(entry[0]));
    }
  }

  return [];
}

export function rgbaForLabelValue(
  value: number,
  analysisType: AnalysisType | undefined,
  config: Record<string, any> | undefined,
  layer?: ImageLayerKey,
): [number, number, number, number] {
  const colors = config?.colors ?? {};
  if (isAorticAnalysis(analysisType)) {
    const aortaColor = config?.aorta_colors?.[value];
    const configuredOpacity = Number(config?.aorta_opacitys?.[value]?.[1]);
    if (
      Array.isArray(aortaColor) &&
      aortaColor.length >= 4 &&
      Number.isFinite(configuredOpacity) &&
      configuredOpacity > 0
    ) {
      return [
        Number(aortaColor[1]) || 0,
        Number(aortaColor[2]) || 0,
        Number(aortaColor[3]) || 0,
        Math.round(Math.max(0, Math.min(1, configuredOpacity)) * 255),
      ];
    }
    if (layer === 'aorta') return hexToRgba(colors.aorta, 128);
    if (layer === 'avc') return hexToRgba(colors.avc, 128);
  }

  const alpha = 128;
  switch (value) {
    case 1:
      return hexToRgba(colors.lowAttenuation, alpha);
    case 2:
      return hexToRgba(colors.nonCalcified, alpha);
    case 3:
      return hexToRgba(colors.CALCIUM, alpha);
    case 251:
      return hexToRgba(colors.FAI4, alpha);
    case 252:
      return hexToRgba(colors.FAI3, alpha);
    case 253:
      return hexToRgba(colors.FAI2, alpha);
    case 254:
      return hexToRgba(colors.FAI1, alpha);
    case 255:
      return hexToRgba(colors.LUMEN, alpha);
    default:
      return [255, 255, 255, alpha];
  }
}

function outlineRgbaForLayer(
  layer: ImageLayerKey,
  config: Record<string, any> | undefined,
): [number, number, number, number] {
  const colors = config?.colors ?? {};
  if (layer === 'lumenoutline') return hexToRgba(colors.lumenoutline, 230);
  if (layer === 'plaqueoutline') return hexToRgba(colors.walloutline, 230);
  return [255, 255, 255, 230];
}

function isOutlineLayer(layer: ImageLayerKey) {
  return layer === 'lumenoutline' || layer === 'plaqueoutline';
}

const labelOverlayIsLittleEndian = (() => {
  const buffer = new ArrayBuffer(4);
  new Uint32Array(buffer)[0] = 0x0a0b0c0d;
  return new Uint8Array(buffer)[0] === 0x0d;
})();

const labelOverlayAlphaCountByRgba = new WeakMap<Uint8ClampedArray, number>();

function packRgbaForUint32([r, g, b, a]: [number, number, number, number]) {
  return labelOverlayIsLittleEndian
    ? ((a << 24) | (b << 16) | (g << 8) | r) >>> 0
    : ((r << 24) | (g << 16) | (b << 8) | a) >>> 0;
}

function canWriteRgbaAsUint32(rgba: Uint8ClampedArray) {
  return labelOverlayIsLittleEndian && rgba.byteOffset % 4 === 0;
}

export function paintLabelMap({
  labelMap,
  layer,
  analysisType,
  config,
  labelValues,
}: {
  labelMap: LabelMap2D;
  layer: ImageLayerKey;
  analysisType?: AnalysisType;
  config?: Record<string, any>;
  labelValues?: number[];
}) {
  const allowedLayers = getAllowedRasterImageLayersForAnalysisType(analysisType);
  if (allowedLayers && !allowedLayers.has(layer)) return null;

  const values = labelValues?.length
    ? labelValues
    : getVisibleLabelValuesForLayer(analysisType, layer, config);
  if (!values.length) return null;

  const width = labelMap.width;
  const height = labelMap.height;
  const pixelCount = width * height;
  const source = labelMap.data;
  const rgba = new Uint8ClampedArray(pixelCount * 4);
  const rgba32 = canWriteRgbaAsUint32(rgba)
    ? new Uint32Array(rgba.buffer, rgba.byteOffset, pixelCount)
    : null;
  const selected = new Uint8Array(256);
  values.forEach((value) => {
    if (Number.isFinite(value) && value >= 0 && value <= 255) {
      selected[value & 0xff] = 1;
    }
  });

  let nonZeroAlpha = 0;

  if (isOutlineLayer(layer)) {
    const color = outlineRgbaForLayer(layer, config);
    if (rgba32) {
      const packed = packRgbaForUint32(color);
      for (let y = 0; y < height; y += 1) {
        const rowOffset = y * width;
        for (let x = 0; x < width; x += 1) {
          const index = rowOffset + x;
          const value = source[index];
          if (!selected[value]) continue;

          const isBoundary =
            x === 0 ||
            y === 0 ||
            x === width - 1 ||
            y === height - 1 ||
            !selected[source[index - 1]] ||
            !selected[source[index + 1]] ||
            !selected[source[index - width]] ||
            !selected[source[index + width]];
          if (!isBoundary) continue;

          rgba32[index] = packed;
          nonZeroAlpha += 1;
        }
      }
    } else {
      const [r, g, b, a] = color;
      for (let y = 0; y < height; y += 1) {
        const rowOffset = y * width;
        for (let x = 0; x < width; x += 1) {
          const index = rowOffset + x;
          const value = source[index];
          if (!selected[value]) continue;

          const isBoundary =
            x === 0 ||
            y === 0 ||
            x === width - 1 ||
            y === height - 1 ||
            !selected[source[index - 1]] ||
            !selected[source[index + 1]] ||
            !selected[source[index - width]] ||
            !selected[source[index + width]];
          if (!isBoundary) continue;

          const offset = index * 4;
          rgba[offset] = r;
          rgba[offset + 1] = g;
          rgba[offset + 2] = b;
          rgba[offset + 3] = a;
          nonZeroAlpha += 1;
        }
      }
    }

    labelOverlayAlphaCountByRgba.set(rgba, nonZeroAlpha);
    return rgba;
  }

  const colorByValue32 = new Uint32Array(256);
  const alphaByValue = new Uint8Array(256);
  const colorByValueRgba = new Array<[number, number, number, number] | undefined>(
    256,
  );
  for (let value = 0; value < selected.length; value += 1) {
    if (!selected[value]) continue;
    const color = rgbaForLabelValue(value, analysisType, config, layer);
    if (color[3] === 0) continue;
    colorByValue32[value] = packRgbaForUint32(color);
    colorByValueRgba[value] = color;
    alphaByValue[value] = 1;
  }

  if (rgba32) {
    for (let index = 0; index < pixelCount; index += 1) {
      const value = source[index];
      if (!selected[value] || !alphaByValue[value]) continue;
      rgba32[index] = colorByValue32[value];
      nonZeroAlpha += 1;
    }
  } else {
    for (let index = 0; index < pixelCount; index += 1) {
      const value = source[index];
      if (!selected[value] || !alphaByValue[value]) continue;
      const color = colorByValueRgba[value];
      if (!color) continue;
      const offset = index * 4;
      rgba[offset] = color[0];
      rgba[offset + 1] = color[1];
      rgba[offset + 2] = color[2];
      rgba[offset + 3] = color[3];
      nonZeroAlpha += 1;
    }
  }

  labelOverlayAlphaCountByRgba.set(rgba, nonZeroAlpha);
  return rgba;
}

function scaleRgbaNearest({
  rgba,
  sourceWidth,
  sourceHeight,
  targetWidth,
  targetHeight,
}: {
  rgba: Uint8ClampedArray;
  sourceWidth: number;
  sourceHeight: number;
  targetWidth: number;
  targetHeight: number;
}) {
  const safeTargetWidth = Math.max(1, Math.floor(targetWidth));
  const safeTargetHeight = Math.max(1, Math.floor(targetHeight));
  if (sourceWidth === safeTargetWidth && sourceHeight === safeTargetHeight) {
    return {
      rgba,
      width: sourceWidth,
      height: sourceHeight,
    };
  }

  const scaled = new Uint8ClampedArray(safeTargetWidth * safeTargetHeight * 4);
  for (let y = 0; y < safeTargetHeight; y += 1) {
    const sourceY = Math.min(
      sourceHeight - 1,
      Math.floor((y / safeTargetHeight) * sourceHeight),
    );
    for (let x = 0; x < safeTargetWidth; x += 1) {
      const sourceX = Math.min(
        sourceWidth - 1,
        Math.floor((x / safeTargetWidth) * sourceWidth),
      );
      const sourceOffset = (sourceY * sourceWidth + sourceX) * 4;
      const targetOffset = (y * safeTargetWidth + x) * 4;
      scaled[targetOffset] = rgba[sourceOffset];
      scaled[targetOffset + 1] = rgba[sourceOffset + 1];
      scaled[targetOffset + 2] = rgba[sourceOffset + 2];
      scaled[targetOffset + 3] = rgba[sourceOffset + 3];
    }
  }

  return {
    rgba: scaled,
    width: safeTargetWidth,
    height: safeTargetHeight,
  };
}

export function blobFromRgba(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
): Promise<Blob | null> {
  if (typeof document === 'undefined') return Promise.resolve(null);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.resolve(null);
  ctx.putImageData(new ImageData(rgba, width, height), 0, 0);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
}

function countNonZeroAlpha(rgba: Uint8ClampedArray) {
  const cached = labelOverlayAlphaCountByRgba.get(rgba);
  if (cached !== undefined) return cached;
  let count = 0;
  for (let offset = 3; offset < rgba.length; offset += 4) {
    if (rgba[offset] !== 0) count += 1;
  }
  return count;
}

function logServerLabelOverlayDiagnostic(
  event: string,
  payload: Record<string, unknown>,
) {
  if (!(globalThis as any).__CARDIOLUCID_IMAGE_RESOLVE_DEBUG__) return;
  console.debug('[server-label-overlay]', event, payload);
}

function inferLabelSourceDimensions(data: Uint8Array, tile?: ImageTileRect) {
  const square = Math.sqrt(data.byteLength);
  if (Number.isInteger(square) && square > 0) {
    return { width: square, height: square };
  }
  if (tile) {
    const width = Math.max(tile.x + tile.width, tile.width);
    const height = Math.max(1, Math.floor(data.byteLength / width));
    if (width * height <= data.byteLength) return { width, height };
  }
  return null;
}

function cropLabelTile(
  data: Uint8Array,
  sourceWidth: number,
  sourceHeight: number,
  tile: ImageTileRect,
) {
  const x0 = Math.max(0, Math.floor(tile.x));
  const y0 = Math.max(0, Math.floor(tile.y));
  const width = Math.max(1, Math.floor(tile.width));
  const height = Math.max(1, Math.floor(tile.height));
  const out = new Uint8Array(width * height);
  for (let y = 0; y < height; y += 1) {
    const sourceY = y0 + y;
    if (sourceY < 0 || sourceY >= sourceHeight) continue;
    const sourceOffset = sourceY * sourceWidth + x0;
    const targetOffset = y * width;
    out.set(
      data.slice(
        sourceOffset,
        Math.min(sourceOffset + width, data.byteLength),
      ),
      targetOffset,
    );
  }
  return { data: out, width, height };
}

function resizeLabelNearest(
  data: Uint8Array,
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number,
) {
  const safeTargetWidth = Math.max(1, Math.floor(targetWidth));
  const safeTargetHeight = Math.max(1, Math.floor(targetHeight));
  if (sourceWidth === safeTargetWidth && sourceHeight === safeTargetHeight) {
    return { data, width: sourceWidth, height: sourceHeight };
  }
  const out = new Uint8Array(safeTargetWidth * safeTargetHeight);
  for (let y = 0; y < safeTargetHeight; y += 1) {
    const sourceY = Math.min(
      sourceHeight - 1,
      Math.floor((y / safeTargetHeight) * sourceHeight),
    );
    for (let x = 0; x < safeTargetWidth; x += 1) {
      const sourceX = Math.min(
        sourceWidth - 1,
        Math.floor((x / safeTargetWidth) * sourceWidth),
      );
      out[y * safeTargetWidth + x] = data[sourceY * sourceWidth + sourceX];
    }
  }
  return { data: out, width: safeTargetWidth, height: safeTargetHeight };
}

function flipLabelRows(data: Uint8Array, width: number, height: number) {
  const out = new Uint8Array(data.length);
  for (let y = 0; y < height; y += 1) {
    const sourceOffset = y * width;
    const targetOffset = (height - 1 - y) * width;
    out.set(data.subarray(sourceOffset, sourceOffset + width), targetOffset);
  }
  return out;
}

function prepareServerLabelMapData({
  data,
  width,
  height,
  transform,
}: {
  data: Uint8Array;
  width: number;
  height: number;
  transform?: {
    tileRect?: ImageTileRect;
    outputWidth?: number;
    outputHeight?: number;
    flipY?: boolean;
  };
}): LabelMap2D | null {
  const tile = transform?.tileRect;
  let currentData = data;
  let currentWidth = width;
  let currentHeight = height;

  if (tile) {
    const sourceDims = inferLabelSourceDimensions(data, tile);
    if (!sourceDims) return null;
    const cropped = cropLabelTile(
      data,
      sourceDims.width,
      sourceDims.height,
      tile,
    );
    currentData = cropped.data;
    currentWidth = cropped.width;
    currentHeight = cropped.height;
  } else if (data.byteLength < width * height) {
    return null;
  } else if (data.byteLength !== width * height) {
    currentData = data.slice(0, width * height);
  }

  const resized = resizeLabelNearest(
    currentData,
    currentWidth,
    currentHeight,
    transform?.outputWidth ?? width,
    transform?.outputHeight ?? height,
  );
  currentData = resized.data;
  currentWidth = resized.width;
  currentHeight = resized.height;

  if (transform?.flipY) {
    currentData = flipLabelRows(currentData, currentWidth, currentHeight);
  }

  return {
    width: currentWidth,
    height: currentHeight,
    data: currentData,
  };
}

function windowHuToByte(hu: number, currentVOI?: VOI): number {
  const windowLevel = currentVOI?.windowLevel ?? 40;
  const windowWidth = Math.max(1, currentVOI?.windowWidth ?? 400);
  if (!Number.isFinite(hu)) return 0;
  const normalized = (hu - (windowLevel - 0.5)) / (windowWidth - 1) + 0.5;
  return Math.round(Math.max(0, Math.min(1, normalized)) * 255);
}

function cprHuToRgba(
  result: { width: number; height: number; data: Int16Array },
  currentVOI?: VOI,
) {
  const rgba = new Uint8ClampedArray(result.width * result.height * 4);
  let target = 0;
  for (let i = 0; i < result.data.length; i += 1) {
    const value = windowHuToByte(result.data[i], currentVOI);
    rgba[target] = value;
    rgba[target + 1] = value;
    rgba[target + 2] = value;
    rgba[target + 3] = 255;
    target += 4;
  }
  return rgba;
}

function imageDataToLabelMap(
  imageData: vtkImageData | null | undefined,
): LabelMap2D | null {
  if (!imageData) return null;
  const dims = imageData.getDimensions?.();
  const scalars = imageData.getPointData?.().getScalars?.().getData?.();
  if (!dims || !scalars) return null;
  const data =
    scalars instanceof Uint8Array
      ? scalars
      : ArrayBuffer.isView(scalars)
        ? Uint8Array.from(scalars as ArrayLike<number>, (value) =>
            Math.max(0, Math.min(255, Math.round(Number(value) || 0))),
          )
        : Uint8Array.from(scalars as number[]);
  return {
    width: dims[0],
    height: dims[1],
    data,
  };
}

function getCprHuMap(
  context: LocalOverlayContext,
  parsed: Extract<ParsedOverlayPath, { kind: 'cpr' }>,
  width: number,
  height: number,
): { width: number; height: number; data: Int16Array } | null {
  const imageData = context.huImageData;
  const centerlineData = context.centerDataByBranch?.[parsed.branchId];
  if (!imageData || !centerlineData) return null;

  const vtkRenderer =
    require('@kitware/vtk.js/Rendering/Core/Renderer').default;
  const { radiansFromDegrees } = require('@kitware/vtk.js/Common/Core/Math');
  const { vec3 } = require('gl-matrix');
  const { createCPRActorAndMapper, rotateView } = require('utils/vtkUtilites');
  const { exportCPRHU } = require('utils/CPRUtilities/CPRUtilities');
  const renderer = vtkRenderer.newInstance();
  const axis = vec3.fromValues(0, 0, 1);
  const savedTranslation = vec3.fromValues(0, 0, 0);

  try {
    const { mapper, actor } = createCPRActorAndMapper({
      data: imageData,
      mode: parsed.mode,
      width,
      centerline: centerlineData,
      currentVOI: context.currentVOI ?? { windowLevel: 40, windowWidth: 400 },
    });
    try {
      renderer.addActor(actor);
      rotateView({
        radian: radiansFromDegrees(parsed.angleDeg),
        mapper: mapper as any,
        actor,
        renderer,
        savedTranslation,
        axis,
        mode: parsed.mode,
      });
      return exportCPRHU(mapper as any, imageData, {
        outWidthPx: width,
        outHeightPx: height,
        projectionAxis: parsed.mode === 'stretched' ? 'bitangent' : 'normal',
        angle: parsed.angleDeg,
      });
    } finally {
      renderer.removeActor(actor);
      mapper?.delete?.();
      actor?.delete?.();
    }
  } catch {
    return null;
  } finally {
    renderer.delete?.();
  }
}

function getLabelData(
  context: LocalOverlayContext,
  branchId: number,
): vtkImageData | undefined {
  if (isAorticAnalysis(context.analysisType)) {
    return context.aorticLabelData ?? context.labelDataByBranch?.[branchId];
  }
  return context.labelDataByBranch?.[branchId];
}

function getCrossSectionLabelMap(
  context: RegisteredLocalOverlayContext,
  parsed: Extract<ParsedOverlayPath, { kind: 'cross-section' }>,
  width: number,
  height: number,
): LabelMap2D | null {
  const cacheKey = [
    parsed.seriesId,
    context.version,
    parsed.branchId,
    parsed.index,
    Math.max(1, Math.floor(width)),
    Math.max(1, Math.floor(height)),
    context.analysisType ?? 'unknown',
  ].join('|');
  if (crossSectionLabelMapCache.has(cacheKey)) {
    return crossSectionLabelMapCache.get(cacheKey) ?? null;
  }

  const labelData = getLabelData(context, parsed.branchId);
  const centerData = context.centerDataByBranch?.[parsed.branchId];
  const pool = context.pool;
  if (!labelData || !centerData || !pool || !Number.isFinite(parsed.index)) {
    crossSectionLabelMapCache.set(cacheKey, null);
    return null;
  }

  try {
    const { CalculateResliceAxes } = require('utils/vtkUtilites');
    const vtkImageReslice =
      require('@kitware/vtk.js/Imaging/Core/ImageReslice').default;
    const {
      InterpolationMode,
    } = require('@kitware/vtk.js/Imaging/Core/AbstractImageInterpolator/Constants');
    const config = context.config ?? {};
    const requestedSize = Math.max(
      1,
      Math.floor(Math.min(width, height) || 256),
    );
    const baseWidth = requestedSize;
    const axes = CalculateResliceAxes(
      pool as any,
      parsed.branchId,
      parsed.index,
      context.analysisType,
      centerData,
      labelData,
    );
    const size = Math.max(
      1,
      Math.min(
        256,
        Math.floor(
          Number.isFinite(baseWidth) && baseWidth > 0 ? baseWidth : 256,
        ),
      ),
    );
    const half = Math.floor(size * 0.5);
    const spacing = labelData.getSpacing?.() ?? [1, 1, 1];
    const outputOriginOffset = Math.floor(half * spacing[0]);
    const reslice = vtkImageReslice.newInstance();

    try {
      reslice.setTransformInputSampling(false);
      reslice.setAutoCropOutput(false);
      reslice.setOutputDimensionality(2);
      reslice.setOutputScalarType('Uint8Array');
      reslice.setOutputSpacing(spacing);
      reslice.setOutputOrigin([-outputOriginOffset, -outputOriginOffset, 0]);
      reslice.setOutputExtent([0, half * 2 - 1, 0, half * 2 - 1, 0, 0]);
      reslice.setResliceAxes(axes);
      reslice.setInputData(labelData);
      reslice.setInterpolationMode(InterpolationMode.NEAREST);
      const labelMap = imageDataToLabelMap(reslice.getOutputData());
      crossSectionLabelMapCache.set(cacheKey, labelMap);
      return labelMap;
    } finally {
      reslice?.delete?.();
    }
  } catch {
    crossSectionLabelMapCache.set(cacheKey, null);
    return null;
  }
}

function getCprLabelMap(
  context: LocalOverlayContext,
  parsed: Extract<ParsedOverlayPath, { kind: 'cpr' }>,
  width: number,
  height: number,
): LabelMap2D | null {
  const labelData = getLabelData(context, parsed.branchId);
  const centerlineData = context.centerDataByBranch?.[parsed.branchId];
  if (!labelData || !centerlineData) return null;

  const vtkRenderer =
    require('@kitware/vtk.js/Rendering/Core/Renderer').default;
  const { radiansFromDegrees } = require('@kitware/vtk.js/Common/Core/Math');
  const { vec3 } = require('gl-matrix');
  const { createCPRActorAndMapper, rotateView } = require('utils/vtkUtilites');
  const { exportCPRLabelMap } = require('utils/CPRUtilities/CPRUtilities');
  const renderer = vtkRenderer.newInstance();
  const axis = vec3.fromValues(0, 0, 1);
  const savedTranslation = vec3.fromValues(0, 0, 0);
  try {
    const { mapper, actor } = createCPRActorAndMapper({
      data: labelData,
      mode: parsed.mode,
      width,
      centerline: centerlineData,
      currentVOI: context.currentVOI ?? { windowLevel: 40, windowWidth: 400 },
      isPaintData: true,
    });
    try {
      renderer.addActor(actor);
      rotateView({
        radian: radiansFromDegrees(parsed.angleDeg),
        mapper: mapper as any,
        actor,
        renderer,
        savedTranslation,
        axis,
        mode: parsed.mode,
      });
      const labelMap = exportCPRLabelMap(mapper as any, labelData, {
        outWidthPx: width,
        outHeightPx: height,
        useCenterlineRows: false,
        projectionAxis: parsed.mode === 'stretched' ? 'bitangent' : 'normal',
      });
      return {
        width: labelMap.width,
        height: labelMap.height,
        data: labelMap.data,
      };
    } finally {
      renderer.removeActor(actor);
      mapper?.delete?.();
      actor?.delete?.();
    }
  } catch {
    return null;
  } finally {
    renderer.delete?.();
  }
}

function stableStringify(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableStringify(entry)).join(',')}]`;
  }
  const object = value as Record<string, unknown>;
  return `{${Object.keys(object)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(object[key])}`)
    .join(',')}}`;
}

function objectRefId(value: unknown) {
  if (!value || (typeof value !== 'object' && typeof value !== 'function')) {
    return 'none';
  }
  const object = value as object;
  let id = objectIdentity.get(object);
  if (!id) {
    id = nextObjectIdentity;
    nextObjectIdentity += 1;
    objectIdentity.set(object, id);
  }
  return String(id);
}

function recordRefSignature(record: Record<number, unknown> | undefined) {
  if (!record) return 'none';
  return Object.keys(record)
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => `${key}:${objectRefId((record as Record<string, unknown>)[key])}`)
    .join('|');
}

function localOverlayConfigSignature(config: Record<string, any> | undefined) {
  if (!config) return 'none';
  return stableStringify({
    EditMode: config.EditMode ?? null,
    label: config.label ?? null,
    colors: config.colors ?? null,
    avc_indexs: config.avc_indexs ?? null,
    aorta_colors: config.aorta_colors ?? null,
    aorta_indexs: config.aorta_indexs ?? null,
    aorta_opacitys: config.aorta_opacitys ?? null,
    CPR_CROSS_SECTION_CPR_LOCAL_OVERLAY:
      config.CPR_CROSS_SECTION_CPR_LOCAL_OVERLAY ?? null,
  });
}

function localOverlayContextSignature(context: LocalOverlayContext) {
  return stableStringify({
    analysisType: context.analysisType ?? null,
    currentVOI: {
      windowLevel: context.currentVOI?.windowLevel ?? null,
      windowWidth: context.currentVOI?.windowWidth ?? null,
      invert: context.currentVOI?.invert === true,
    },
    config: localOverlayConfigSignature(context.config),
    huImageData: objectRefId(context.huImageData),
    aorticLabelData: objectRefId(context.aorticLabelData),
    labelDataByBranch: recordRefSignature(context.labelDataByBranch),
    centerDataByBranch: recordRefSignature(context.centerDataByBranch),
    forceCprLocalOverlayFallback:
      context.forceCprLocalOverlayFallback === true,
  });
}

function mergeLocalOverlayContext(
  previous: RegisteredLocalOverlayContext | undefined,
  next: LocalOverlayContext,
): LocalOverlayContext {
  if (!previous) return next;
  return {
    analysisType: next.analysisType ?? previous.analysisType,
    config: next.config ?? previous.config,
    currentVOI: next.currentVOI ?? previous.currentVOI,
    pool: next.pool ?? previous.pool,
    huImageData: next.huImageData ?? previous.huImageData,
    labelDataByBranch: next.labelDataByBranch ?? previous.labelDataByBranch,
    centerDataByBranch: next.centerDataByBranch ?? previous.centerDataByBranch,
    aorticLabelData: next.aorticLabelData ?? previous.aorticLabelData,
    forceCprLocalOverlayFallback:
      next.forceCprLocalOverlayFallback ?? previous.forceCprLocalOverlayFallback,
  };
}

function areVoiValuesEqual(a: VOI | undefined, b: VOI | undefined) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.windowLevel === b.windowLevel &&
    a.windowWidth === b.windowWidth &&
    a.invert === b.invert
  );
}

function areLocalOverlayContextsEquivalent(
  previous: RegisteredLocalOverlayContext | undefined,
  next: LocalOverlayContext,
  nextSignature: string,
) {
  if (!previous) return false;
  if (previous.signature === nextSignature) return true;
  return (
    previous.analysisType === next.analysisType &&
    areVoiValuesEqual(previous.currentVOI, next.currentVOI) &&
    previous.huImageData === next.huImageData &&
    previous.labelDataByBranch === next.labelDataByBranch &&
    previous.centerDataByBranch === next.centerDataByBranch &&
    previous.aorticLabelData === next.aorticLabelData &&
    previous.forceCprLocalOverlayFallback === next.forceCprLocalOverlayFallback &&
    localOverlayConfigSignature(previous.config) ===
      localOverlayConfigSignature(next.config)
  );
}

export function registerLocalOverlayImageContext(
  seriesId: string | undefined,
  context: LocalOverlayContext,
) {
  if (!seriesId) return;
  const previousContext = contexts.get(seriesId);
  const nextContext = mergeLocalOverlayContext(previousContext, context);
  const signature = localOverlayContextSignature(nextContext);
  if (areLocalOverlayContextsEquivalent(previousContext, nextContext, signature)) {
    return;
  }
  for (const key of crossSectionLabelMapCache.keys()) {
    if (key.startsWith(`${seriesId}|`)) {
      crossSectionLabelMapCache.delete(key);
    }
  }
  const nextVersion = (contextVersion += 1);
  contexts.set(seriesId, {
    ...nextContext,
    version: nextVersion,
    signature,
  });
  notifyLocalOverlayImageContextVersion(seriesId, nextVersion);
}

function notifyLocalOverlayImageContextVersion(
  seriesId: string,
  version: number,
) {
  contextVersionListeners.get(seriesId)?.forEach((listener) => {
    listener(version);
  });
}

export function getLocalOverlayImageContextVersion(
  seriesId: string | undefined,
) {
  if (!seriesId) return 0;
  return contexts.get(seriesId)?.version ?? 0;
}

export function subscribeLocalOverlayImageContextVersion(
  seriesId: string | undefined,
  listener: (version: number) => void,
) {
  if (!seriesId) return () => undefined;
  let listeners = contextVersionListeners.get(seriesId);
  if (!listeners) {
    listeners = new Set();
    contextVersionListeners.set(seriesId, listeners);
  }
  listeners.add(listener);
  return () => {
    listeners?.delete(listener);
    if (listeners?.size === 0) {
      contextVersionListeners.delete(seriesId);
    }
  };
}

export function createServerLabelOverlayRgbaImage({
  layer,
  labelData,
  width,
  height,
  transform,
  labelValues,
  overlayPolicy,
}: {
  seriesId?: string;
  layer: ImageLayerKey;
  labelData: ArrayBuffer | Uint8Array;
  width: number;
  height: number;
  transform?: {
    tileRect?: ImageTileRect;
    outputWidth?: number;
    outputHeight?: number;
    flipY?: boolean;
  };
  labelValues?: number[];
  overlayPolicy?: OverlayPaintPolicy;
}): RgbaImageData | null {
  if (!overlayPolicy) {
    logServerLabelOverlayDiagnostic('missing-policy', { layer });
    return null;
  }
  const safeWidth = Math.max(
    1,
    Math.floor(transform?.outputWidth ?? width),
  );
  const safeHeight = Math.max(
    1,
    Math.floor(transform?.outputHeight ?? height),
  );
  const data =
    labelData instanceof Uint8Array
      ? labelData
      : new Uint8Array(labelData);
  const prepared = prepareServerLabelMapData({
    data,
    width: safeWidth,
    height: safeHeight,
    transform,
  });
  if (!prepared) {
    logServerLabelOverlayDiagnostic('prepare-failed', {
      layer,
      bytes: data.byteLength,
      width: safeWidth,
      height: safeHeight,
      transform,
    });
    return null;
  }
  const rgba = paintLabelMap({
    labelMap: {
      width: prepared.width,
      height: prepared.height,
      data: prepared.data,
    },
    layer,
    analysisType: overlayPolicy.analysisType,
    config: overlayPolicy.config,
    labelValues,
  });
  if (!rgba) {
    logServerLabelOverlayDiagnostic('paint-failed', {
      layer,
      analysisType: overlayPolicy.analysisType,
      labelValues,
    });
    return null;
  }
  const nonZeroAlpha = countNonZeroAlpha(rgba);
  if (nonZeroAlpha === 0) {
    logServerLabelOverlayDiagnostic('transparent', {
      layer,
      analysisType: overlayPolicy.analysisType,
      labelValues,
    });
    return null;
  }
  logServerLabelOverlayDiagnostic('painted', {
    layer,
    analysisType: overlayPolicy.analysisType,
    labelValues,
    nonZeroAlpha,
  });
  return {
    rgba,
    width: prepared.width,
    height: prepared.height,
  };
}

export function createServerLabelOverlayImageBlob({
  layer,
  labelData,
  width,
  height,
  transform,
  labelValues,
  overlayPolicy,
}: {
  seriesId?: string;
  layer: ImageLayerKey;
  labelData: ArrayBuffer | Uint8Array;
  width: number;
  height: number;
  transform?: {
    tileRect?: ImageTileRect;
    outputWidth?: number;
    outputHeight?: number;
    flipY?: boolean;
  };
  labelValues?: number[];
  overlayPolicy?: OverlayPaintPolicy;
}): Promise<Blob | null> {
  const image = createServerLabelOverlayRgbaImage({
    layer,
    labelData,
    width,
    height,
    transform,
    labelValues,
    overlayPolicy,
  });
  return image ? blobFromRgba(image.rgba, image.width, image.height) : Promise.resolve(null);
}

export function unregisterLocalOverlayImageContext(
  seriesId: string | undefined,
) {
  if (!seriesId) return;
  contexts.delete(seriesId);
  notifyLocalOverlayImageContextVersion(seriesId, 0);
}

export function isLocalOverlayImagePath(path: string) {
  return path.startsWith(LOCAL_OVERLAY_PREFIX);
}

export function createLocalOverlayPath({
  kind,
  seriesId,
  branchId,
  mode,
  angleDeg,
  index,
  layer,
}: {
  kind: 'cpr' | 'cross-section';
  seriesId: string;
  branchId: number;
  mode?: CprMode;
  angleDeg?: number;
  index?: number;
  layer: ImageLayerKey;
}) {
  if (kind === 'cpr') {
    return `${LOCAL_OVERLAY_PREFIX}cpr/${seriesId}/${branchId}/${mode}/${angleDeg ?? 0}/${layer}`;
  }
  return `${LOCAL_OVERLAY_PREFIX}cross-section/${seriesId}/${branchId}/${index ?? 0}/${layer}`;
}

export function withLocalOverlayFallbackLayers(
  layers: ResolvedImageLayerPaths | null,
  input: {
    seriesId?: string;
    kind: 'cpr' | 'cross-section';
    branchId: number;
    mode?: CprMode;
    angleDeg?: number;
    index?: number;
    enableCprLocalOverlayFallback?: boolean;
  },
): ResolvedImageLayerPaths | null {
  if (!layers || !input.seriesId) return layers;
  if (input.kind === 'cpr' && !input.enableCprLocalOverlayFallback) {
    return layers;
  }
  const next: ResolvedImageLayerPaths = { ...layers };
  OVERLAY_LAYER_ORDER.forEach((layer) => {
    if (next[layer]) return;
    next[layer] = createLocalOverlayPath({
      ...input,
      seriesId: input.seriesId!,
      layer,
    });
  });
  return next;
}

export async function createLocalOverlayImageBlob({
  path,
  width,
  height,
}: {
  path: string;
  width: number;
  height: number;
}): Promise<Blob | null> {
  const parsed = parsePath(path);
  if (!parsed) return null;
  const context = contexts.get(parsed.seriesId);
  if (!context) return null;
  if (parsed.layer === 'base') {
    if (parsed.kind !== 'cpr') return null;
    const huMap = getCprHuMap(context, parsed, width, height);
    if (!huMap) return null;
    return blobFromRgba(
      cprHuToRgba(huMap, context.currentVOI),
      huMap.width,
      huMap.height,
    );
  }
  if (
    parsed.kind === 'cpr' &&
    !context.forceCprLocalOverlayFallback &&
    !isCprLocalOverlayFallbackEnabled(context.config)
  ) {
    return null;
  }

  const labelMap =
    parsed.kind === 'cross-section'
      ? getCrossSectionLabelMap(context, parsed, width, height)
      : getCprLabelMap(context, parsed, width, height);
  if (!labelMap) return null;

  const rgba = paintLabelMap({
    labelMap,
    layer: parsed.layer,
    analysisType: context.analysisType,
    config: context.config,
  });
  if (!rgba) return null;
  const scaled =
    parsed.kind === 'cross-section'
      ? scaleRgbaNearest({
          rgba,
          sourceWidth: labelMap.width,
          sourceHeight: labelMap.height,
          targetWidth: width,
          targetHeight: height,
        })
      : { rgba, width: labelMap.width, height: labelMap.height };
  return blobFromRgba(scaled.rgba, scaled.width, scaled.height);
}
